import type { ComponentChildren } from "preact";
import { memo } from "preact/compat";
import { shortAddr } from "../../wallets/lib/format";
import { type CsvValue, fileName, saveFile, toCsv } from "../lib/csv";
import { copyText } from "../lib/format";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParam } from "../lib/nav";

export interface Col<T> {
	key: string;
	label: string;
	title?: string;
	/** Minimum width in px. */
	w: number;
	/** Share of spare width. Columns without it stay at `w`. */
	grow?: number;
	right?: boolean;
	/** Columns are dropped from the highest number down until the rest fit. 0 never drops. */
	prio?: number;
	/** Rule to the left of this column, to separate groups such as lifetime and 30-day figures. */
	sep?: boolean;
	/** Left out on phones, for columns another cell already covers there. */
	wideOnly?: boolean;
	cell: (row: T, narrow: boolean) => ComponentChildren;
	sort?: (row: T) => number | string | null | undefined;
	/** The value exported to CSV, when it should differ from `sort`. False leaves the column out. */
	csv?: ((row: T) => CsvValue) | false;
}

export type Sort = { key: string; dir: "asc" | "desc" };

export interface GridProps<T> {
	cols: Col<T>[];
	rows: T[];
	rowKey: (row: T) => string;
	initialSort?: Sort;
	/** Row height on phones, for rows with a second line. */
	narrowRowH?: number;
	/** A full-width second line under the row on phones, where columns have no room to explain. */
	subline?: (row: T) => ComponentChildren;
	/** Query param that holds the sort, so it survives a shared link and Back. */
	sortParam?: string;
	onRow?: (row: T) => void;
	/** Replaces the body; the header stays so the layout does not jump. */
	body?: ComponentChildren;
	loading?: boolean;
	label: string;
	/** File name stem. Set it to offer CSV of the rows as filtered and sorted. */
	csvName?: string;
	/** What the c key copies from the selected row, usually a wallet address. */
	copyValue?: (row: T) => string | null | undefined;
}

const PAD_X = 16;
const COL_GAP = 10;
const NARROW = 640;
const OVERSCAN = 10;
// The window moves in steps smaller than the overscan: scrolling inside a step renders nothing.
const WINDOW_STEP_ROWS = 5;
const FLASH_MS = 2_000;
const HEADER_H = 28;

// Where each list was scrolled to, so Back lands on the row you left.
const scrollMemory = new Map<string, number>();
const scrollKey = () => {
	const q = new URLSearchParams(window.location.search);
	q.delete("m");
	q.delete("mt");
	return `${window.location.pathname}?${q}`;
};

/** The columns that fit `width`, dropping by priority. Exported for tests. */
/** The rows as given, every column that has a value to export, on screen or not (pure). */
export function gridCsv<T>(cols: Col<T>[], rows: T[]): string {
	const out = cols.flatMap((c) => {
		const get = c.csv === false ? undefined : (c.csv ?? c.sort);
		return get && c.label ? [{ label: c.label, get }] : [];
	});
	return toCsv(
		out.map((c) => c.label),
		rows.map((r) => out.map((c) => c.get(r))),
	);
}

export function fitColumns<T>(cols: Col<T>[], width: number): Col<T>[] {
	const kept = [...cols];
	const need = (list: Col<T>[]) =>
		list.reduce((s, c) => s + c.w, 0) + COL_GAP * (list.length - 1) + PAD_X;
	while (need(kept) > width) {
		let worst = -1;
		for (let i = 0; i < kept.length; i++) {
			const p = kept[i]?.prio ?? 0;
			if (p > 0 && (worst < 0 || p >= (kept[worst]?.prio ?? 0))) worst = i;
		}
		if (worst < 0) break;
		kept.splice(worst, 1);
	}
	return kept;
}

export function compareBy<T>(get: (r: T) => number | string | null | undefined, dir: "asc" | "desc") {
	const sign = dir === "asc" ? 1 : -1;
	return (a: T, b: T): number => {
		const x = get(a);
		const y = get(b);
		const xNone = x === null || x === undefined || x === "" || Number.isNaN(x);
		const yNone = y === null || y === undefined || y === "" || Number.isNaN(y);
		if (xNone || yNone) return xNone === yNone ? 0 : xNone ? 1 : -1; // gaps stay last
		if (typeof x === "number" && typeof y === "number") return (x - y) * sign;
		return String(x).localeCompare(String(y)) * sign;
	};
}

export const sortToParam = (s: Sort): string => `${s.key}.${s.dir}`;

/** The sort a param asks for, if it names a sortable column (pure). */
export function parseSort<T>(param: string, cols: Col<T>[]): Sort | undefined {
	const dot = param.lastIndexOf(".");
	const key = param.slice(0, dot);
	const dir = param.slice(dot + 1);
	if (dir !== "asc" && dir !== "desc") return undefined;
	return cols.some((c) => c.key === key && c.sort) ? { key, dir } : undefined;
}

/**
 * Sorted rows. Ties fall back to the row key, so equal rows hold their order when a refresh
 * delivers them differently. Without a sort the given order stands (pure).
 */
export function sortRows<T>(rows: T[], col: Col<T> | undefined, dir: "asc" | "desc", rowKey: (r: T) => string): T[] {
	if (!col?.sort) return rows;
	const cmp = compareBy(col.sort, dir);
	return [...rows].sort((a, b) => {
		const c = cmp(a, b);
		if (c !== 0) return c;
		const x = rowKey(a);
		const y = rowKey(b);
		return x < y ? -1 : x > y ? 1 : 0;
	});
}

/** The next selected index for a j/k press (pure). Starts at the top when nothing is selected. */
/**
 * How many rows the one at `index` moved between two lists, so a scrolled reader can stay on it.
 * Zero when it is gone (pure).
 */
export function anchorShift<T>(before: T[], after: T[], index: number, rowKey: (r: T) => string): number {
	const row = before[index];
	if (row === undefined) return 0;
	const key = rowKey(row);
	const at = after.findIndex((r) => rowKey(r) === key);
	return at < 0 ? 0 : at - index;
}

export function stepIndex(current: number, delta: number, count: number): number {
	if (count === 0) return -1;
	if (current < 0) return delta > 0 ? 0 : count - 1;
	return Math.min(count - 1, Math.max(0, current + delta));
}

const isTyping = (t: EventTarget | null) =>
	(t as HTMLElement | null)?.closest?.("input, textarea, select, [contenteditable]") != null;
const isControl = (t: EventTarget | null) => (t as HTMLElement | null)?.closest?.("a, button, summary") != null;

function SkeletonRows({ template, cols, rowH }: { template: string; cols: number; rowH: number }) {
	return (
		<div aria-hidden="true">
			{Array.from({ length: 14 }, (_, r) => (
				<div
					key={r}
					className="grid items-center border-b border-line/50 px-2"
					style={{ gridTemplateColumns: template, columnGap: COL_GAP, height: rowH }}
				>
					{Array.from({ length: cols }, (_, c) => (
						<div
							key={c}
							className="skeleton-bar h-2"
							style={{ width: c === 0 ? `${55 + ((r * 13) % 40)}%` : "70%" }}
						/>
					))}
				</div>
			))}
		</div>
	);
}

interface RowProps<T> {
	row: T;
	index: number;
	on: boolean;
	cols: Col<T>[];
	template: string;
	rowH: number;
	narrow: boolean;
	subline?: (row: T) => ComponentChildren;
	clickable: boolean;
	pick: (row: T) => void;
}

function GridRowInner<T>({ row, index, on, cols, template, rowH, narrow, subline, clickable, pick }: RowProps<T>) {
	return (
		<div
			role="row"
			id={`row-${index}`}
			aria-selected={on}
			onClick={() => pick(row)}
			className={`flex flex-col justify-center border-b border-line/50 px-2 text-[11px] text-fg ${clickable ? "cursor-pointer hover:bg-raised" : ""} ${on ? "bg-raised shadow-[inset_2px_0_0_var(--color-up)]" : ""}`}
			style={{ height: rowH }}
		>
			<div className="grid items-center" style={{ gridTemplateColumns: template, columnGap: COL_GAP }}>
				{cols.map((c) => (
					<div
						role="cell"
						key={c.key}
						className={`num min-w-0 ${narrow && c.grow ? "" : "truncate"} ${c.right ? "text-right" : ""} ${c.sep ? "border-l border-line pl-2" : ""}`}
					>
						{c.cell(row, narrow)}
					</div>
				))}
			</div>
			{narrow && subline ? <div className="num truncate text-[10px] leading-4 text-dim">{subline(row)}</div> : null}
		</div>
	);
}

// Rows still in the window when it moves are left alone.
const GridRow = memo(GridRowInner) as typeof GridRowInner;

export function DataGrid<T>({ cols, rows, rowKey, initialSort, narrowRowH = 42, subline, sortParam = "sort", onRow, body, loading, label, csvName, copyValue }: GridProps<T>) {
	const scroller = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(1200);
	const [viewH, setViewH] = useState(600);
	const [top, setTop] = useState(0);
	const [sortText, setSortText] = useParam(sortParam);
	const [selected, setSelected] = useState<string | null>(null);
	const sort = parseSort(sortText, cols) ?? initialSort;
	const sortId = sort ? sortToParam(sort) : "";

	useLayoutEffect(() => {
		const el = scroller.current;
		if (!el) return;
		const measure = () => {
			setWidth(el.clientWidth);
			setViewH(el.clientHeight);
		};
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const narrow = width < NARROW;
	const rowH = narrow ? narrowRowH : 27;
	const shown = useMemo(() => fitColumns(narrow ? cols.filter((c) => !c.wideOnly) : cols, width), [cols, width, narrow]);
	const template = shown
		.map((c) => (c.grow ? `minmax(${c.w}px, ${c.grow}fr)` : `${c.w}px`))
		.join(" ");

	// rowKey is usually an inline arrow, which would rerun this on every render.
	const keyOf = useRef(rowKey);
	keyOf.current = rowKey;
	const sorted = useMemo(() => {
		const col = sort && cols.find((c) => c.key === sort.key);
		return sortRows(rows, col, sort?.dir ?? "desc", (r) => keyOf.current(r));
	}, [rows, cols, sortId]);

	const rowCount = sorted.length;
	const hasRows = !body && !loading && rowCount > 0;

	// Rows arriving above a scrolled reader must not push what they are reading down the screen.
	// At the very top they stay put, so new rows show up.
	const previous = useRef({ sorted, sortId });
	useLayoutEffect(() => {
		const was = previous.current;
		previous.current = { sorted, sortId };
		const el = scroller.current;
		if (!el || was.sorted === sorted || was.sortId !== sortId || el.scrollTop <= 0) return;
		const shift = anchorShift(was.sorted, sorted, Math.floor(el.scrollTop / rowH), (r) => keyOf.current(r));
		if (shift !== 0) el.scrollTop += shift * rowH;
	}, [sorted, sortId, rowH]);

	// A filter that shrinks the list must not leave the window past its end.
	useEffect(() => {
		const el = scroller.current;
		if (el && el.scrollTop > rowCount * rowH) el.scrollTop = 0;
	}, [rowCount, rowH]);

	// A new sort starts from the top, but not on arrival, when Back may be restoring a position.
	const lastSort = useRef(sortId);
	useEffect(() => {
		if (lastSort.current === sortId) return;
		lastSort.current = sortId;
		if (scroller.current) scroller.current.scrollTop = 0;
	}, [sortId]);

	const restored = useRef(false);
	useLayoutEffect(() => {
		const el = scroller.current;
		if (!el || restored.current || !hasRows) return;
		restored.current = true;
		const saved = scrollMemory.get(scrollKey());
		if (saved) {
			el.scrollTop = saved;
			setTop(el.scrollTop);
		}
	}, [hasRows]);

	// Selection follows the row, not the position, so a refresh that reorders keeps it.
	const lastIndex = useRef(-1);
	const selectedIndex = selected === null ? -1 : sorted.findIndex((r) => rowKey(r) === selected);
	if (selectedIndex >= 0) lastIndex.current = selectedIndex;

	const [flash, setFlash] = useState("");
	const flashTimer = useRef<number | undefined>(undefined);
	const say = useCallback((text: string) => {
		setFlash(text);
		window.clearTimeout(flashTimer.current);
		flashTimer.current = window.setTimeout(() => setFlash(""), FLASH_MS);
	}, []);
	useEffect(() => () => window.clearTimeout(flashTimer.current), []);

	const keys = useRef({ sorted, selectedIndex, rowH, viewH, hasRows, onRow, copyValue });
	keys.current = { sorted, selectedIndex, rowH, viewH, hasRows, onRow, copyValue };

	useEffect(() => {
		const reveal = (i: number) => {
			const el = scroller.current;
			if (!el) return;
			const { rowH: h, viewH: view } = keys.current;
			const start = i * h;
			if (start < el.scrollTop) el.scrollTop = start;
			else if (start + h + HEADER_H > el.scrollTop + view) el.scrollTop = start + h + HEADER_H - view;
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
			if (document.querySelector('[role="dialog"]')) return; // a drawer or palette has the keyboard
			if (document.querySelector('[role="table"]') !== scroller.current) return;
			const k = keys.current;
			if (!k.hasRows) return;
			if (e.key === "c" && k.copyValue) {
				const row = k.sorted[k.selectedIndex];
				const value = row === undefined ? null : k.copyValue(row);
				if (!value) say("select a row first");
				else void copyText(value).then((done) => say(done ? `copied ${shortAddr(value)}` : "could not copy: the browser refused"));
				return;
			}
			const inGrid = scroller.current?.contains(e.target as Node) ?? false;
			const delta = e.key === "j" || (e.key === "ArrowDown" && inGrid) ? 1 : e.key === "k" || (e.key === "ArrowUp" && inGrid) ? -1 : 0;
			if (delta) {
				e.preventDefault();
				const from = k.selectedIndex >= 0 ? k.selectedIndex : Math.min(lastIndex.current, k.sorted.length - 1);
				const next = stepIndex(from, delta, k.sorted.length);
				const row = k.sorted[next];
				if (row === undefined) return;
				setSelected(keyOf.current(row));
				reveal(next);
			} else if (e.key === "Enter" && !isControl(e.target)) {
				const row = k.sorted[k.selectedIndex];
				if (row !== undefined && k.onRow) {
					e.preventDefault();
					k.onRow(row);
				}
			} else if (e.key === "Escape") {
				setSelected(null);
				lastIndex.current = -1;
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	const first = Math.max(0, Math.floor(top / rowH) - OVERSCAN);
	const last = Math.min(rowCount, Math.ceil((top + viewH) / rowH) + WINDOW_STEP_ROWS + OVERSCAN);
	const windowed = sorted.slice(first, last);

	const pick = useCallback((row: T) => {
		setSelected(keyOf.current(row));
		keys.current.onRow?.(row);
	}, []);

	const onHeader = (c: Col<T>) => {
		if (!c.sort) return;
		const dir = sort?.key === c.key ? (sort.dir === "desc" ? "asc" : "desc") : c.right ? "desc" : "asc";
		setSortText(sortToParam({ key: c.key, dir }));
	};

	const exportCsv = () => {
		if (!csvName) return;
		saveFile(fileName(csvName, "csv", new Date()), gridCsv(cols, sorted), "text/csv;charset=utf-8");
		say(`saved ${sorted.length} rows`);
	};

	return (
		<>
		<div
			ref={scroller}
			role="table"
			aria-label={label}
			aria-rowcount={rowCount}
			tabIndex={0}
			aria-activedescendant={selectedIndex >= 0 ? `row-${selectedIndex}` : undefined}
			className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-panel focus:outline-none"
			// A scrollbar arriving with the rows must not shift the columns.
			style={{ scrollbarGutter: "stable" }}
			onScroll={(e) => {
				const y = (e.currentTarget as HTMLDivElement).scrollTop;
				const step = rowH * WINDOW_STEP_ROWS;
				setTop(Math.floor(y / step) * step);
				if (restored.current) scrollMemory.set(scrollKey(), y);
			}}
		>
			<div
				role="row"
				className="sticky top-0 z-10 grid h-7 items-center border-b border-line bg-[#070a0e] px-2 text-[10px] uppercase tracking-wide text-dim"
				style={{ gridTemplateColumns: template, columnGap: COL_GAP }}
			>
				{shown.map((c) => {
					const on = sort?.key === c.key;
					return (
						<button
							type="button"
							role="columnheader"
							key={c.key}
							title={c.title}
							disabled={!c.sort}
							aria-sort={on ? (sort?.dir === "asc" ? "ascending" : "descending") : undefined}
							onClick={() => onHeader(c)}
							className={`truncate uppercase ${c.right ? "text-right" : "text-left"} ${on ? "text-bright" : ""} ${c.sort ? "cursor-pointer hover:text-bright" : "cursor-default"} ${c.sep ? "border-l border-line pl-2" : ""}`}
						>
							{c.label}
							{on ? (sort?.dir === "asc" ? " ↑" : " ↓") : ""}
						</button>
					);
				})}
			</div>

			{body ??
				(loading ? (
					<SkeletonRows template={template} cols={shown.length} rowH={rowH} />
				) : (
					<div style={{ height: rowCount * rowH, position: "relative" }}>
						<div style={{ transform: `translateY(${first * rowH}px)` }}>
							{windowed.map((row, i) => (
								<GridRow
									key={rowKey(row)}
									row={row}
									index={first + i}
									on={first + i === selectedIndex}
									cols={shown}
									template={template}
									rowH={rowH}
									narrow={narrow}
									subline={subline}
									clickable={Boolean(onRow)}
									pick={pick}
								/>
							))}
						</div>
					</div>
				))}
		</div>
		{/* Always there, so loading and error states do not change the grid's height. */}
		{csvName || copyValue ? (
			<div className="flex h-5 shrink-0 items-center gap-3 border-t border-line bg-ink px-2 text-[10px] text-mute">
				<span className="hidden truncate sm:inline">
					j/k move{onRow ? " · enter open" : ""}
					{copyValue ? " · c copy address" : ""}
				</span>
				<span role="status" className="ml-auto truncate text-dim">
					{flash}
				</span>
				{csvName ? (
					<button type="button" disabled={!hasRows} onClick={exportCsv} className="shrink-0 text-dim hover:text-up disabled:opacity-40">
						export csv
					</button>
				) : null}
			</div>
		) : null}
		</>
	);
}
